import React, { useState, useRef, useEffect } from 'react';
import Layout from '../components/Layout';
import { Camera, Upload, Save, History, Trash2, Scan, Video, Image as ImageIcon, RefreshCw, CheckCircle, Pill, X, Plus, Minus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PillCounter = () => {
  const { currentUser } = useAuth();
  const [pillCounts, setPillCounts] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [detectedCount, setDetectedCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [mode, setMode] = useState('menu'); // 'menu', 'live', 'snapshot', 'manual'
  const [liveCount, setLiveCount] = useState(0);
  const [manualCount, setManualCount] = useState(0);
  
  const [saveForm, setSaveForm] = useState({
    petName: '',
    medication: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const liveCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [stream, setStream] = useState(null);
  const animationFrameRef = useRef(null);

  // Load saved pill counts from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`pillCounts_${currentUser?.uid}`);
    if (saved) {
      setPillCounts(JSON.parse(saved));
    }
  }, [currentUser]);

  // Save pill counts to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`pillCounts_${currentUser.uid}`, JSON.stringify(pillCounts));
    }
  }, [pillCounts, currentUser]);

  // Attach stream to video when available
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play()
          .then(() => {
            if (mode === 'live') {
              startLiveCounting();
            }
          })
          .catch(err => {
            console.error('Error playing video:', err);
          });
      };
    }
  }, [stream, mode]);

  // Advanced Pill Detection Algorithm
  const detectPills = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Convert to grayscale and apply edge detection
    const grayscale = new Uint8ClampedArray(canvas.width * canvas.height);
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      grayscale[i / 4] = gray;
    }
    
    // Find blobs (pill candidates) using connected components
    const blobs = findBlobs(grayscale, canvas.width, canvas.height);
    
    // Filter blobs by size and shape (pills are roughly circular/oval)
    const pills = blobs.filter(blob => {
      const area = blob.pixels.length;
      const minArea = 100;
      const maxArea = 5000;
      
      if (area < minArea || area > maxArea) return false;
      
      const perimeter = calculatePerimeter(blob);
      const circularity = (4 * Math.PI * area) / (perimeter * perimeter);
      
      return circularity > 0.3;
    });
    
    return pills;
  };

  // Find connected components (blobs) in the image
  const findBlobs = (grayscale, width, height) => {
    const visited = new Array(width * height).fill(false);
    const blobs = [];
    
    const threshold = calculateOtsuThreshold(grayscale);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        
        if (visited[idx]) continue;
        
        const current = grayscale[idx];
        const neighbors = [
          grayscale[idx - 1],
          grayscale[idx + 1],
          grayscale[idx - width],
          grayscale[idx + width]
        ];
        
        const avgNeighbor = neighbors.reduce((a, b) => a + b, 0) / neighbors.length;
        const isEdge = Math.abs(current - avgNeighbor) > threshold;
        
        if (isEdge) {
          const blob = floodFill(grayscale, visited, x, y, width, height, threshold);
          if (blob.pixels.length > 50) {
            blobs.push(blob);
          }
        }
      }
    }
    
    return blobs;
  };

  // Flood fill to find connected pixels
  const floodFill = (grayscale, visited, startX, startY, width, height, threshold) => {
    const stack = [[startX, startY]];
    const pixels = [];
    let minX = startX, maxX = startX, minY = startY, maxY = startY;
    
    while (stack.length > 0) {
      const [x, y] = stack.pop();
      const idx = y * width + x;
      
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (visited[idx]) continue;
      
      visited[idx] = true;
      pixels.push([x, y]);
      
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      
      const neighbors = [
        [x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1],
        [x - 1, y - 1], [x + 1, y - 1], [x - 1, y + 1], [x + 1, y + 1]
      ];
      
      for (const [nx, ny] of neighbors) {
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          const nIdx = ny * width + nx;
          if (!visited[nIdx]) {
            const current = grayscale[idx];
            const neighbor = grayscale[nIdx];
            if (Math.abs(current - neighbor) < threshold) {
              stack.push([nx, ny]);
            }
          }
        }
      }
    }
    
    return { pixels, minX, maxX, minY, maxY };
  };

  // Calculate Otsu's threshold
  const calculateOtsuThreshold = (grayscale) => {
    const histogram = new Array(256).fill(0);
    for (let i = 0; i < grayscale.length; i++) {
      histogram[Math.floor(grayscale[i])]++;
    }
    
    let total = grayscale.length;
    let sum = 0;
    for (let i = 0; i < 256; i++) {
      sum += i * histogram[i];
    }
    
    let sumB = 0;
    let wB = 0;
    let maxVariance = 0;
    let threshold = 0;
    
    for (let i = 0; i < 256; i++) {
      wB += histogram[i];
      if (wB === 0) continue;
      
      let wF = total - wB;
      if (wF === 0) break;
      
      sumB += i * histogram[i];
      const mB = sumB / wB;
      const mF = (sum - sumB) / wF;
      
      const variance = wB * wF * (mB - mF) * (mB - mF);
      
      if (variance > maxVariance) {
        maxVariance = variance;
        threshold = i;
      }
    }
    
    return threshold * 0.15;
  };

  // Calculate perimeter of a blob
  const calculatePerimeter = (blob) => {
    let perimeter = 0;
    const pixelSet = new Set(blob.pixels.map(([x, y]) => `${x},${y}`));
    
    for (const [x, y] of blob.pixels) {
      const neighbors = [
        [x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]
      ];
      
      for (const [nx, ny] of neighbors) {
        if (!pixelSet.has(`${nx},${ny}`)) {
          perimeter++;
        }
      }
    }
    
    return perimeter;
  };

  // Start camera
  const startCamera = async (selectedMode) => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 1280, height: 720 }
      });
      
      setStream(mediaStream);
      setMode(selectedMode);
    } catch (error) {
      console.error('Camera error:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  // Live counting loop
  const startLiveCounting = () => {
    const countFrame = () => {
      if (!videoRef.current || !liveCanvasRef.current) return;
      
      const video = videoRef.current;
      const canvas = liveCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      ctx.drawImage(video, 0, 0);
      
      const pills = detectPills(canvas);
      setLiveCount(pills.length);
      
      // Draw detection overlay
      ctx.strokeStyle = '#10B981';
      ctx.lineWidth = 2;
      pills.forEach(pill => {
        const centerX = (pill.minX + pill.maxX) / 2;
        const centerY = (pill.minY + pill.maxY) / 2;
        const radius = Math.max(pill.maxX - pill.minX, pill.maxY - pill.minY) / 2;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2);
        ctx.stroke();
      });
      
      animationFrameRef.current = requestAnimationFrame(countFrame);
    };
    
    countFrame();
  };

  // Take snapshot
  const takeSnapshot = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsProcessing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    const pills = detectPills(canvas);
    
    // Draw circles around detected pills
    ctx.strokeStyle = '#10B981';
    ctx.lineWidth = 3;
    pills.forEach(pill => {
      const centerX = (pill.minX + pill.maxX) / 2;
      const centerY = (pill.minY + pill.maxY) / 2;
      const radius = Math.max(pill.maxX - pill.minX, pill.maxY - pill.minY) / 2;
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2);
      ctx.stroke();
    });
    
    setCurrentImage(canvas.toDataURL('image/jpeg'));
    setDetectedCount(pills.length);
    stopCamera();
    setMode('menu');
    setIsProcessing(false);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsProcessing(true);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const pills = detectPills(canvas);
        
        // Draw circles around detected pills
        ctx.strokeStyle = '#10B981';
        ctx.lineWidth = 3;
        pills.forEach(pill => {
          const centerX = (pill.minX + pill.maxX) / 2;
          const centerY = (pill.minY + pill.maxY) / 2;
          const radius = Math.max(pill.maxX - pill.minX, pill.maxY - pill.minY) / 2;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2);
          ctx.stroke();
        });
        
        setCurrentImage(canvas.toDataURL('image/jpeg'));
        setDetectedCount(pills.length);
        setIsProcessing(false);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Save pill count
  const savePillCount = () => {
    const newCount = {
      id: Date.now(),
      petName: saveForm.petName,
      medication: saveForm.medication,
      count: detectedCount || manualCount,
      notes: saveForm.notes,
      date: saveForm.date,
      image: currentImage,
      createdAt: new Date().toISOString()
    };
    
    setPillCounts([newCount, ...pillCounts]);
    setShowSaveForm(false);
    setSaveForm({
      petName: '',
      medication: '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
    setCurrentImage(null);
    setDetectedCount(0);
    setManualCount(0);
    
    alert('✅ Pill count saved successfully!');
  };

  // Delete pill count
  const deletePillCount = (id) => {
    setPillCounts(pillCounts.filter(c => c.id !== id));
  };

  // Reset
  const reset = () => {
    stopCamera();
    setCurrentImage(null);
    setDetectedCount(0);
    setLiveCount(0);
    setManualCount(0);
    setMode('menu');
    setShowSaveForm(false);
  };

  // Capture live count
  const captureLiveCount = () => {
    setDetectedCount(liveCount);
    stopCamera();
    setMode('menu');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-pastel-pink via-pastel-blue to-pastel-purple rounded-2xl p-6 shadow-md">
          <h1 className="text-2xl font-bold text-brand-brown flex items-center gap-2">
            💊 Pet Medication Counter
          </h1>
          <p className="text-brand-brown/70 mt-1">
            Count and track your pet's medications easily
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Main Counter Section */}
          <div className="space-y-6">
            {/* Mode Selection Menu */}
            {mode === 'menu' && !currentImage && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
                <h2 className="text-xl font-bold text-brand-brown mb-4 flex items-center gap-2">
                  <Scan className="w-6 h-6 text-pastel-purple" />
                  Count Pills
                </h2>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Live Count */}
                  <button
                    onClick={() => startCamera('live')}
                    className="p-6 bg-gradient-to-br from-pastel-green/30 to-pastel-blue/30 rounded-xl border-2 border-pastel-green hover:scale-105 transition-transform"
                  >
                    <Video className="w-10 h-10 text-green-600 mx-auto mb-3" />
                    <p className="font-bold text-brand-brown">Live Count</p>
                    <p className="text-xs text-brand-brown/60 mt-1">Real-time detection</p>
                  </button>

                  {/* Snapshot */}
                  <button
                    onClick={() => startCamera('snapshot')}
                    className="p-6 bg-gradient-to-br from-pastel-pink/30 to-pastel-purple/30 rounded-xl border-2 border-pastel-purple hover:scale-105 transition-transform"
                  >
                    <Camera className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                    <p className="font-bold text-brand-brown">Take Photo</p>
                    <p className="text-xs text-brand-brown/60 mt-1">Capture & count</p>
                  </button>

                  {/* Upload */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-6 bg-gradient-to-br from-pastel-blue/30 to-pastel-pink/30 rounded-xl border-2 border-pastel-blue hover:scale-105 transition-transform"
                  >
                    <Upload className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                    <p className="font-bold text-brand-brown">Upload Photo</p>
                    <p className="text-xs text-brand-brown/60 mt-1">From gallery</p>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                  />

                  {/* Manual Count */}
                  <button
                    onClick={() => setMode('manual')}
                    className="p-6 bg-gradient-to-br from-pastel-yellow/30 to-pastel-pink/30 rounded-xl border-2 border-yellow-400 hover:scale-105 transition-transform"
                  >
                    <Plus className="w-10 h-10 text-yellow-600 mx-auto mb-3" />
                    <p className="font-bold text-brand-brown">Manual Count</p>
                    <p className="text-xs text-brand-brown/60 mt-1">Enter count manually</p>
                  </button>
                </div>
              </div>
            )}

            {/* Processing Indicator */}
            {isProcessing && (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple mx-auto mb-4"></div>
                <p className="text-lg font-bold text-brand-brown">Counting pills...</p>
                <p className="text-sm text-brand-brown/60">AI detection in progress</p>
              </div>
            )}

            {/* Live View */}
            {mode === 'live' && stream && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-brand-brown">Live Counting</h2>
                  <button
                    onClick={reset}
                    className="p-2 hover:bg-gray-100 rounded-lg text-red-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="relative rounded-xl overflow-hidden mb-4">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full" />
                  <canvas ref={liveCanvasRef} className="absolute top-0 left-0 w-full h-full pointer-events-none" />
                  
                  {/* Live Count Badge */}
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-pastel-pink to-pastel-purple text-white px-4 py-2 rounded-full font-bold shadow-lg">
                    {liveCount} pills
                  </div>
                </div>

                <button
                  onClick={captureLiveCount}
                  className="w-full bg-gradient-to-r from-pastel-green to-green-400 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Capture Count ({liveCount} pills)
                </button>
              </div>
            )}

            {/* Snapshot View */}
            {mode === 'snapshot' && stream && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-brand-brown">Take Photo</h2>
                  <button
                    onClick={reset}
                    className="p-2 hover:bg-gray-100 rounded-lg text-red-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="relative rounded-xl overflow-hidden mb-4">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full" />
                </div>

                <button
                  onClick={takeSnapshot}
                  className="w-full bg-gradient-to-r from-pastel-purple to-purple-400 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  Take Photo & Count
                </button>
              </div>
            )}

            {/* Manual Count Mode */}
            {mode === 'manual' && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-brand-brown">Manual Count</h2>
                  <button
                    onClick={reset}
                    className="p-2 hover:bg-gray-100 rounded-lg text-red-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="bg-gradient-to-r from-pastel-pink to-pastel-purple rounded-xl p-6 text-center mb-4">
                  <p className="text-sm text-white/80 mb-2">Pill Count</p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setManualCount(Math.max(0, manualCount - 1))}
                      className="w-14 h-14 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white"
                    >
                      <Minus className="w-6 h-6" />
                    </button>
                    <span className="text-6xl font-bold text-white">{manualCount}</span>
                    <button
                      onClick={() => setManualCount(manualCount + 1)}
                      className="w-14 h-14 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white"
                    >
                      <Plus className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <input
                    type="number"
                    value={manualCount}
                    onChange={(e) => setManualCount(Math.max(0, parseInt(e.target.value) || 0))}
                    className="flex-1 px-4 py-3 border-2 border-brand-brown/20 rounded-xl text-center text-xl font-bold"
                    min="0"
                  />
                </div>

                <button
                  onClick={() => {
                    setDetectedCount(manualCount);
                    setShowSaveForm(true);
                  }}
                  disabled={manualCount === 0}
                  className="w-full mt-4 bg-gradient-to-r from-pastel-green to-green-400 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  Save Count ({manualCount} pills)
                </button>
              </div>
            )}

            {/* Results Display */}
            {currentImage && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-brand-brown">Detection Results</h2>
                  <button
                    onClick={reset}
                    className="text-red-500 hover:text-red-600 flex items-center gap-1"
                  >
                    <RefreshCw size={16} />
                    <span className="text-sm">New Count</span>
                  </button>
                </div>

                <img
                  src={currentImage}
                  alt="Detected Pills"
                  className="w-full rounded-xl mb-4"
                />

                {/* Count Display */}
                <div className="bg-gradient-to-r from-pastel-pink to-pastel-purple text-white rounded-xl p-6 text-center mb-4">
                  <p className="text-sm uppercase tracking-wide mb-2">Pills Detected</p>
                  <p className="text-6xl font-bold">{detectedCount}</p>
                  <p className="text-sm mt-2 text-white/80">Automatically counted with AI</p>
                </div>

                {!showSaveForm && (
                  <button
                    onClick={() => setShowSaveForm(true)}
                    className="w-full bg-gradient-to-r from-pastel-green to-green-400 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Save size={20} />
                    Save Count
                  </button>
                )}
              </div>
            )}

            {/* Save Form */}
            {showSaveForm && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
                <h3 className="text-lg font-bold text-brand-brown mb-4">Save Pill Count</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Pet Name *
                    </label>
                    <input
                      type="text"
                      value={saveForm.petName}
                      onChange={(e) => setSaveForm({...saveForm, petName: e.target.value})}
                      className="w-full p-3 border-2 border-brand-brown/20 rounded-xl focus:border-pastel-purple outline-none"
                      placeholder="e.g., Max, Bella"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Medication Name *
                    </label>
                    <input
                      type="text"
                      value={saveForm.medication}
                      onChange={(e) => setSaveForm({...saveForm, medication: e.target.value})}
                      className="w-full p-3 border-2 border-brand-brown/20 rounded-xl focus:border-pastel-purple outline-none"
                      placeholder="e.g., Amoxicillin 250mg"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Notes (optional)
                    </label>
                    <textarea
                      value={saveForm.notes}
                      onChange={(e) => setSaveForm({...saveForm, notes: e.target.value})}
                      className="w-full p-3 border-2 border-brand-brown/20 rounded-xl focus:border-pastel-purple outline-none"
                      rows="2"
                      placeholder="e.g., Monthly heartworm prevention"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={saveForm.date}
                      onChange={(e) => setSaveForm({...saveForm, date: e.target.value})}
                      className="w-full p-3 border-2 border-brand-brown/20 rounded-xl focus:border-pastel-purple outline-none"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={savePillCount}
                      disabled={!saveForm.petName || !saveForm.medication}
                      className="flex-1 bg-gradient-to-r from-pastel-green to-green-400 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowSaveForm(false);
                        if (mode === 'manual') {
                          reset();
                        }
                      }}
                      className="px-6 bg-gray-200 text-brand-brown py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Hidden canvas */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          {/* History Column */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-brand-brown/10">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-pastel-purple" />
              <h2 className="text-lg font-bold text-brand-brown">Count History</h2>
            </div>

            {pillCounts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-pastel-purple/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Pill className="w-8 h-8 text-pastel-purple" />
                </div>
                <p className="text-brand-brown/60">No saved counts yet</p>
                <p className="text-sm text-brand-brown/40 mt-2">
                  Count some pills to get started!
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[700px] overflow-y-auto">
                {pillCounts.map(count => (
                  <div 
                    key={count.id} 
                    className="bg-gradient-to-br from-pastel-pink/20 to-pastel-purple/20 rounded-xl p-4 border-2 border-pastel-purple/30"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🐾</span>
                          <h3 className="font-bold text-brand-brown">{count.petName}</h3>
                        </div>
                        <p className="text-sm font-medium text-pastel-purple">{count.medication}</p>
                        <p className="text-xs text-brand-brown/50">
                          {new Date(count.date).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => deletePillCount(count.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="bg-gradient-to-r from-pastel-pink to-pastel-purple text-white rounded-lg p-3 mb-2">
                      <p className="text-2xl font-bold text-center">
                        {count.count} pills
                      </p>
                    </div>

                    {count.notes && (
                      <p className="text-sm text-brand-brown/70 mb-2">
                        <strong>Notes:</strong> {count.notes}
                      </p>
                    )}

                    {count.image && (
                      <img
                        src={count.image}
                        alt="Pills"
                        className="w-full rounded-lg mt-2 cursor-pointer hover:opacity-90"
                        onClick={() => window.open(count.image, '_blank')}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PillCounter;
